import React from 'react';
import { PaperClipIcon, PlusIcon } from '@heroicons/react/20/solid';
import { Button } from '@oysterjs/ui/button';
import { ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { messageFromError } from '@oysterjs/core/graphql/errors';

import { addEmailBulkSendRecipients } from '~/api/data/email-bulk-send/mutations';
import type { BulkDraft } from '~/api/data/email-bulk-send/queries';
import type { EmailBulkSendRecipientInput } from '~/api/graphql';
import { InlineNotice } from '~/components/inline-notice';
import { pluralize } from '~/lib/pluralize';
import { BulkDraftClientSearch } from './bulk-draft-client-search';
import { BulkDraftCsvImport } from './bulk-draft-csv';

/** The most recipients a bulk email may have; the server refuses more (db.MaxEmailBulkSendRecipients). */
export const MAX_BULK_SEND_RECIPIENTS = 50;

const ADD_FAILURE = "We couldn't add them just now. Try again.";

/** A search that is an address on its own, which Add row starts the new row with. */
const LOOKS_LIKE_EMAIL = /^\S+@\S+$/;

/** What adding left out, in the agent's words; null when every row was added. */
export const leftOutNotice = (result: { duplicatesSkipped: number; overCap: number }) => {
  const { duplicatesSkipped, overCap } = result;
  const sentences = [
    duplicatesSkipped > 0 &&
      `${duplicatesSkipped} ${pluralize(duplicatesSkipped, 'duplicate')} skipped.`,
    overCap > 0 &&
      `Only ${MAX_BULK_SEND_RECIPIENTS} recipients per bulk send. ${overCap} ${pluralize(overCap, 'row')} ${overCap === 1 ? "wasn't" : "weren't"} added.`
  ].filter(Boolean);
  return sentences.length > 0 ? sentences.join(' ') : null;
};

type AddOutcome =
  | { bulkDraft: BulkDraft; added: number; duplicatesSkipped: number; overCap: number }
  | { failure: string };

/** Adds clients to the bulk draft; they wait as Not drafted until Cara drafts them. */
export const addRecipients = async (
  bulkSendId: string,
  recipients: EmailBulkSendRecipientInput[]
): Promise<AddOutcome> => {
  try {
    const result = await addEmailBulkSendRecipients({ id: bulkSendId, recipients });
    const added = result.data?.addEmailBulkSendRecipients;
    if (result.error || !added) {
      return { failure: messageFromError(result.error, ADD_FAILURE) };
    }
    return { ...added, bulkDraft: added.bulkSend };
  } catch (error) {
    return { failure: messageFromError(error, ADD_FAILURE) };
  }
};

/** How the agent adds clients the bulk draft doesn't have yet. */
export const BulkDraftAddRecipients = ({
  bulkDraft,
  onChange
}: {
  bulkDraft: BulkDraft;
  onChange: (bulkDraft: BulkDraft) => void;
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  // The address the new row starts with; null while no row is open.
  const [newRowEmail, setNewRowEmail] = React.useState<string | null>(null);
  const [isUploadingCsv, setIsUploadingCsv] = React.useState(false);
  const [notice, setNotice] = React.useState<{ tone: 'warning' | 'error'; text: string } | null>(
    null
  );
  const recipientCount = bulkDraft.recipients.length;
  const isFull = recipientCount >= MAX_BULK_SEND_RECIPIENTS;

  const showOutcome = (outcome: AddOutcome) => {
    if ('failure' in outcome) {
      setNotice({ tone: 'error', text: outcome.failure });
      return;
    }
    onChange(outcome.bulkDraft);
    const leftOut = leftOutNotice(outcome);
    setNotice(leftOut ? { tone: 'warning', text: leftOut } : null);
  };

  return (
    <section className="mb-4 space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <BulkDraftClientSearch
          recipients={bulkDraft.recipients}
          isFull={isFull}
          query={searchQuery}
          onQueryChange={setSearchQuery}
          onAdd={async (client) => {
            setNotice(null);
            showOutcome(await addRecipients(bulkDraft.id, [client]));
          }}
        />
        <div className="flex w-full flex-wrap items-center gap-3 sm:mt-8 sm:w-auto">
          <Button
            color="white"
            className="whitespace-nowrap"
            disabled={isFull || newRowEmail !== null}
            onClick={() => {
              const searchedFor = searchQuery.trim();
              setNewRowEmail(LOOKS_LIKE_EMAIL.test(searchedFor) ? searchedFor : '');
              setNotice(null);
            }}
          >
            <PlusIcon />
            Add row
          </Button>
          <Button
            color="white"
            className="whitespace-nowrap"
            disabled={isFull && !isUploadingCsv}
            aria-expanded={isUploadingCsv}
            onClick={() => setIsUploadingCsv((isOpen) => !isOpen)}
          >
            <PaperClipIcon />
            Upload CSV
          </Button>
          <div className="ml-auto whitespace-nowrap text-right text-sm text-neutral-500 dark:text-neutral-400">
            {recipientCount} {pluralize(recipientCount, 'recipient')} · up to{' '}
            {MAX_BULK_SEND_RECIPIENTS}
          </div>
        </div>
      </div>
      {newRowEmail !== null && (
        <AddRowForm
          bulkSendId={bulkDraft.id}
          initialEmail={newRowEmail}
          onAdded={(outcome) => {
            setNewRowEmail(null);
            showOutcome(outcome);
          }}
          onCancel={() => setNewRowEmail(null)}
        />
      )}
      {isUploadingCsv && (
        <BulkDraftCsvImport
          recipients={bulkDraft.recipients}
          roomLeft={Math.max(0, MAX_BULK_SEND_RECIPIENTS - recipientCount)}
          addRecipients={async (recipients) => {
            const outcome = await addRecipients(bulkDraft.id, recipients);
            // The import shows its own failure, keeping the file open to try again.
            if ('failure' in outcome) return outcome.failure;
            setIsUploadingCsv(false);
            showOutcome(outcome);
            return undefined;
          }}
        />
      )}
      {notice && <InlineNotice tone={notice.tone} title={notice.text} />}
    </section>
  );
};

/** One client typed in by hand: an address, and a name if the agent has it. */
const AddRowForm = ({
  bulkSendId,
  initialEmail,
  onAdded,
  onCancel
}: {
  bulkSendId: string;
  initialEmail: string;
  onAdded: (outcome: AddOutcome) => void;
  onCancel: () => void;
}) => {
  const [email, setEmail] = React.useState(initialEmail);
  const [name, setName] = React.useState('');
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [addError, setAddError] = React.useState<string | null>(null);
  const [isAdding, setIsAdding] = React.useState(false);

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Enter an email address.');
      return;
    }
    setIsAdding(true);
    setEmailError(null);
    setAddError(null);
    const outcome = await addRecipients(bulkSendId, [
      { email: trimmedEmail, name: name.trim() || undefined }
    ]);
    setIsAdding(false);
    // Kept open on a duplicate or a failure, so what the agent typed isn't lost.
    if ('failure' in outcome) {
      setAddError(outcome.failure);
      return;
    }
    const isDuplicate = outcome.added === 0 && outcome.duplicatesSkipped > 0;
    if (isDuplicate) {
      setEmailError('Already in the list');
      return;
    }
    onAdded(outcome);
  };

  return (
    <form
      onSubmit={add}
      className="flex flex-wrap items-start gap-3 rounded-lg bg-white p-4 shadow dark:bg-neutral-800"
    >
      <Field className="w-full min-w-0 sm:min-w-64 sm:flex-1">
        <Label>Email</Label>
        <Input
          type="email"
          autoFocus
          value={email}
          invalid={!!emailError}
          onChange={(event) => setEmail(event.target.value)}
        />
        {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
      </Field>
      <Field className="w-full min-w-0 sm:min-w-48 sm:flex-1">
        <Label>Name (Optional)</Label>
        <Input value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      {/* Down by a label and its gap, so the buttons stay level with the inputs when an
          error grows the Email field. */}
      <div className="flex gap-2 sm:mt-8">
        <Button color="white" disabled={isAdding} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" color="sky" loading={isAdding}>
          Add
        </Button>
      </div>
      {addError && <InlineNotice tone="error" className="basis-full" title={addError} />}
    </form>
  );
};
